import Hls from 'hls.js';

/**
 * Initialize optimized video loading with HLS support for all videos
 */
export default function InitVideo() {
	// Find all videos that might have HLS sources
	const videos = document.querySelectorAll('video[data-src], video source[src*=".m3u8"]');

	// Process each video element
	const videoElements = new Set();

	videos.forEach(item => {
		const video = item.tagName === 'VIDEO' ? item : item.closest('video');
		if (video) {
			videoElements.add(video);
		}
	});

	// Also check for the hero video specifically
	const heroVideo = document.getElementById('videobanner');
	if (heroVideo) {
		videoElements.add(heroVideo);
	}

	if (videoElements.size === 0) {
		return;
	}

	// Process each unique video element
	videoElements.forEach(video => initializeVideo(video));
}

/**
 * Initialize a single video element with HLS support
 */
function initializeVideo(video) {
	const videoSrc = video.dataset.src || video.querySelector('source')?.src || video.src;

	if (!videoSrc) {
		return;
	}

	// Ensure critical attributes are set for iOS autoplay
	video.muted = true;
	video.playsInline = true;
	video.setAttribute('playsinline', '');
	video.setAttribute('webkit-playsinline', '');
	video.setAttribute('muted', '');

	let hls = null;
	let hasUserInteracted = false;

	// Attempt to play video
	const attemptPlay = () => {
		if (video.paused) {
			const playPromise = video.play();
			if (playPromise !== undefined) {
				playPromise.catch((error) => {
					console.log('Video autoplay was prevented:', error);
					// If blocked, wait for user interaction
					if (!hasUserInteracted) {
						setupUserInteractionHandlers();
					}
				});
			}
		}
	};

	// Setup handlers for user interaction to enable autoplay
	const setupUserInteractionHandlers = () => {
		const enablePlay = () => {
			hasUserInteracted = true;
			video.muted = true; // Ensure still muted
			attemptPlay();
		};

		// Listen for various user interaction events
		const events = ['touchstart', 'touchend', 'click', 'scroll'];
		events.forEach(eventType => {
			document.addEventListener(eventType, enablePlay, { once: true, passive: true });
		});

		// Also try after a short delay (sometimes helps on iOS)
		setTimeout(attemptPlay, 500);
	};

	// Function to load and play video
	const loadVideo = () => {
		// Check if it's an HLS stream (.m3u8)
		if (videoSrc.includes('.m3u8')) {
			// Use HLS.js for browsers that don't support HLS natively
			if (Hls.isSupported()) {
				hls = new Hls({
					startLevel: 0, // Start with lowest quality for fastest load
					autoStartLoad: true,
					maxBufferLength: 4, // Minimal buffer for instant start
					maxMaxBufferLength: 10,
					maxBufferSize: 10 * 1000 * 1000, // 10MB max buffer
					maxBufferHole: 0.5,
					highBufferWatchdogPeriod: 2,
					nudgeOffset: 0.1,
					nudgeMaxRetry: 3,
					maxFragLookUpTolerance: 0.2,
					liveSyncDurationCount: 3,
					liveMaxLatencyDurationCount: 10,
					enableWorker: true, // Use web workers for better performance
					lowLatencyMode: false,
					backBufferLength: 0, // Don't keep old buffer
				});

				hls.loadSource(videoSrc);
				hls.attachMedia(video);

				hls.on(Hls.Events.MANIFEST_PARSED, () => {
					attemptPlay();
				});

				hls.on(Hls.Events.ERROR, (_event, data) => {
					if (data.fatal) {
						console.error('HLS fatal error:', data);
						// Try to recover
						if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
							hls.startLoad();
						} else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
							hls.recoverMediaError();
						}
					}
				});
			}
			// Safari/iOS supports HLS natively
			else if (video.canPlayType('application/vnd.apple.mpegurl')) {
				// Safari-specific optimizations
				video.src = videoSrc;

				// Set playback quality hints for Safari
				if (video.playbackQuality) {
					video.playbackQuality = 'low'; // Start with low quality
				}

				// Force Safari to start loading immediately
				video.load();

				// Wait for enough data before playing
				const playWhenReady = () => {
					if (video.readyState >= 3) { // HAVE_FUTURE_DATA
						attemptPlay();
					} else {
						video.addEventListener('canplay', attemptPlay, { once: true });
					}
				};

				playWhenReady();
			}
		} else {
			// Regular MP4/WebM video
			video.src = videoSrc;
			video.load();
			attemptPlay();
		}
	};

	// Load video immediately - no delays
	loadVideo();

	// Safari-specific: Handle buffering issues
	let isBuffering = false;
	video.addEventListener('waiting', () => {
		isBuffering = true;
	});

	video.addEventListener('playing', () => {
		isBuffering = false;
	});

	video.addEventListener('canplay', () => {
		if (isBuffering && video.paused) {
			video.play().catch(() => {
				// Silently handle autoplay restrictions
			});
		}
	});

	// Prevent choppy playback by maintaining consistent playback rate
	video.addEventListener('progress', () => {
		// Ensure playback rate stays at 1.0 (no speed changes)
		if (video.playbackRate !== 1.0) {
			video.playbackRate = 1.0;
		}
	});

	// Handle visibility change (tab switching)
	const visibilityHandler = () => {
		if (!document.hidden && video.paused) {
			video.play().catch(() => {
				// Silently handle autoplay restrictions
			});
		}
	};
	document.addEventListener('visibilitychange', visibilityHandler);

	// Cleanup HLS instance on page unload
	const cleanupHandler = () => {
		if (hls) {
			hls.destroy();
		}
	};
	window.addEventListener('beforeunload', cleanupHandler);
}
